<script setup>
// REQ-20260813-02: agent × inbox auto-reply gate. Shown only on a single
// inbox + "Mine" tab. Does not rewrite per-conversation auto_reply_enabled;
// the conversation button reads this store so the effective state stays in sync.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  inboxId: { type: [String, Number], required: true },
});

const { t } = useI18n();
const store = useStore();

const enabled = computed({
  get: () => store.getters.agentInboxAutoReplyFor(props.inboxId),
  set: value => {
    store.commit('SET_AGENT_INBOX_AUTO_REPLY', {
      inboxId: props.inboxId,
      enabled: value,
    });
  },
});
const saving = ref(false);

const persist = async () => {
  if (saving.value) return;
  const next = enabled.value;
  saving.value = true;
  try {
    await store.dispatch('setAgentInboxAutoReply', {
      inboxId: props.inboxId,
      enabled: next,
    });
  } catch (error) {
    store.commit('SET_AGENT_INBOX_AUTO_REPLY', {
      inboxId: props.inboxId,
      enabled: !next,
    });
    useAlert(t('CUSTOM.CHAT_LIST.INBOX_AUTO_REPLY_FAILED'));
  } finally {
    saving.value = false;
  }
};

watch(
  () => props.inboxId,
  inboxId => {
    store.dispatch('loadAgentInboxAutoReply', inboxId);
  },
  { immediate: true }
);
</script>

<template>
  <div
    class="flex items-center justify-between gap-3 px-3 py-1.5 border-b border-n-weak"
  >
    <span
      class="min-w-0 text-xs text-n-slate-11 truncate"
      :title="t('CUSTOM.CHAT_LIST.INBOX_AUTO_REPLY_HINT')"
    >
      {{ t('CUSTOM.CHAT_LIST.INBOX_AUTO_REPLY') }}
    </span>
    <div :class="saving && 'pointer-events-none opacity-60'">
      <Switch v-model="enabled" @change="persist" />
    </div>
  </div>
</template>
